import type {
  ApiResponse,
  StartOrganizationCardSetupRequest,
  StartOrganizationCardSetupResult,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import api from '@/libs/axiosInstance';
import { billingContextQueryKey } from '@/hooks/queries/use-billing-context-query';
import { toaster } from '@/components/ui/toaster';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { BillingTargetChangedError, useBillingActionScope } from './use-billing-action-scope';

export const CARD_SETUP_MUTATION_KEY = ['billingCardSetup'] as const;

const resultSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('redirect'),
      operationId: z.string().min(1),
      url: z
        .string()
        .url()
        .refine((url) => new URL(url).protocol === 'https:'),
      expiresAt: z.string().datetime({ offset: true }),
    })
    .strict(),
  z
    .object({
      kind: z.literal('pending'),
      operationId: z.string().min(1),
      retryAfterSeconds: z.number().finite().nonnegative(),
    })
    .strict(),
  z.object({ kind: z.literal('confirmed'), organizationId: z.string().min(1) }).strict(),
]);

export function useStartCardSetupAction() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { scope, assertCurrent } = useBillingActionScope();
  return useMutation<
    StartOrganizationCardSetupResult,
    AxiosError<ApiResponse> | Error,
    StartOrganizationCardSetupRequest,
    typeof scope
  >({
    mutationKey: [...CARD_SETUP_MUTATION_KEY, scope.organizationId, scope.brandId],
    retry: false,
    onMutate: () => {
      assertCurrent(scope);
      return scope;
    },
    mutationFn: async (payload) => {
      assertCurrent(scope);
      if (!scope.organizationId || !scope.brandId) throw new Error('No billing target');
      const { data } = await api.post<ApiResponse<unknown>>(
        '/dashboard/billing/card-setup',
        payload
      );
      assertCurrent(scope);
      const result = resultSchema.safeParse(data.data);
      if (
        data.success !== true ||
        !result.success ||
        (result.data.kind === 'confirmed' && result.data.organizationId !== scope.organizationId)
      ) {
        throw new Error('Invalid card setup response');
      }
      return result.data;
    },
    onSuccess: (_data, _request, target) => {
      if (!target) return;
      try {
        assertCurrent(target);
      } catch {
        return;
      }
      void client.invalidateQueries({
        queryKey: billingContextQueryKey(target),
        exact: true,
      });
    },
    onError: (error) => {
      if (error instanceof BillingTargetChangedError) return;
      toaster.error({ title: t('common.error'), description: t('onboarding.billing.failedBody') });
    },
  });
}
